import React, { useEffect, useRef, useState } from "react";
import { Rnd } from "react-rnd";
import dayjs from "dayjs";
import { FaSearchMinus, FaSearchPlus, FaClock } from "react-icons/fa";
import { AdVideo } from "./AdVideo";
import {
  FaPlay,
  FaPause,
  FaStepBackward,
  FaStepForward,
  FaExpand,
} from "react-icons/fa";

interface Props {
  ads: AdVideo[];
  currentIndex: number;
  onSelectVideo: (index: number) => void;
  onAdsUpdate: (ads: AdVideo[]) => void;
  API_URL: string;
  onPrev: () => void;
  onNext: () => void;
  onPlayPause: () => void;
  onFullscreen: () => void;
  playing: boolean;
}

const TimelineEditor: React.FC<Props> = ({
  ads,
  currentIndex,
  onSelectVideo,
  onAdsUpdate,
  API_URL,
  onPrev,
  onNext,
  onPlayPause,
  onFullscreen,
  playing,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(2);
  const PIXELS_PER_MINUTE = zoom;
  const TOTAL_MINUTES = 24 * 60;
  const timelineWidth = TOTAL_MINUTES * PIXELS_PER_MINUTE;

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const currentMinute = dayjs().hour() * 60 + dayjs().minute();
    container.scrollLeft =
      currentMinute * PIXELS_PER_MINUTE - container.clientWidth / 2;
  }, [zoom]);

  const getMinutes = (date?: string) => {
    if (!date) return 0;
    const d = dayjs(date);
    return d.hour() * 60 + d.minute();
  };

  const minuteToDate = (minute: number) => {
    return dayjs()
      .startOf("day")
      .add(minute, "minute")
      .format("YYYY-MM-DD hh:mm:ss A");
  };

  const updateAdApi = async (ad: AdVideo) => {
    try {
      const formData = new FormData();
      formData.append("start_time", ad.startTime || "");
      formData.append("end_time", ad.endTime || "");
      await fetch(`${API_URL}/videos/${ad.id}/update`, {
        method: "PUT",
        body: formData,
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const data = e.dataTransfer.getData("application/json");
    if (!data) return;
    const media = JSON.parse(data);
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const startMinute = Math.floor(x / PIXELS_PER_MINUTE);
    const endMinute = startMinute + 60;
    const updatedAds = [...ads];
    const existingIndex = updatedAds.findIndex((a) => a.id === media.id);

    if (existingIndex >= 0) {
      updatedAds[existingIndex] = {
        ...updatedAds[existingIndex],
        startTime: minuteToDate(startMinute),
        endTime: minuteToDate(endMinute),
      };
      onAdsUpdate(updatedAds);
      updateAdApi(updatedAds[existingIndex]);
      return;
    }

    const newAd: AdVideo = {
      ...media,
      startTime: minuteToDate(startMinute),
      endTime: minuteToDate(endMinute),
    };
    updatedAds.push(newAd);
    onAdsUpdate(updatedAds);
    updateAdApi(newAd);
  };

  const updatePosition = (index: number, x: number, width: number) => {
    const startMinute = Math.floor(x / PIXELS_PER_MINUTE);
    const duration = Math.floor(width / PIXELS_PER_MINUTE);
    const endMinute = startMinute + duration;
    const updatedAds = [...ads];
    updatedAds[index] = {
      ...updatedAds[index],
      startTime: minuteToDate(startMinute),
      endTime: minuteToDate(endMinute),
    };
    onAdsUpdate(updatedAds);
    updateAdApi(updatedAds[index]);
  };

  const currentMinute = dayjs().hour() * 60 + dayjs().minute();

  return (
    <div
      style={{
        background: "rgb(255, 255, 255)",
        padding: "2px 0px 0px 0px",
        borderRadius: "16px",
        marginTop: "3px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 5,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: "#f0f9ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#0284c8",
            }}
          >
            <FaClock size={13} />
          </div>
          <div>
            <div style={{ color: "#111827", fontSize: 14, fontWeight: 700 }}>
              Timeline Editor
            </div>
            <div style={{ color: "#6b7280", fontSize: 11 }}>
              24 Hour Schedule
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <button style={iconBtn} onClick={onPrev}>
            <FaStepBackward />
          </button>

          <button style={iconBtn} onClick={onPlayPause}>
            {playing ? <FaPause /> : <FaPlay />}
          </button>

          <button style={iconBtn} onClick={onNext}>
            <FaStepForward />
          </button>

          <button style={iconBtn} onClick={onFullscreen}>
            <FaExpand />
          </button>

          <div
            style={{
              width: 1,
              height: 22,
              background: "#ddd",
              marginInline: 4,
            }}
          />

          <button
            style={iconBtn}
            onClick={() => setZoom((z) => Math.max(1, z - 1))}
          >
            <FaSearchMinus />
          </button>

          <button
            style={iconBtn}
            onClick={() => setZoom((z) => Math.min(8, z + 1))}
          >
            <FaSearchPlus />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          borderRadius: 20,
          border: "1px solid #e5e7eb",
          background: "#f8fafc",
          boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
        }}
      >
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          style={{
            position: "relative",
            width: timelineWidth,
            height: 150,
          }}
        >
          {/* Current Time Line */}
          <div
            style={{
              position: "absolute",
              left: currentMinute * PIXELS_PER_MINUTE,
              top: 0,
              width: 2,
              height: "100%",
              background: "#ED6926",
              zIndex: 100,
              boxShadow: "0 0 15px #ef4444",
            }}
          />

          {Array.from({ length: 24 }).map((_, hour) => (
            <div
              key={hour}
              style={{
                position: "absolute",
                left: hour * 60 * PIXELS_PER_MINUTE,
                top: 0,
                width: 60 * PIXELS_PER_MINUTE,
                height: "100%",
                borderLeft: "1px solid #f1f5f9",
              }}
            >
              <div
                style={{
                  height: 38,
                  borderBottom: "1px solid #e2e8f0",
                  background: "#ffffff",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 8,
                    top: 8,
                    color: "#1e2937",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {dayjs().hour(hour).format("h A")}
                </div>
                {[10, 20, 30, 40, 50].map((min) => (
                  <div
                    key={min}
                    style={{
                      position: "absolute",
                      left: min * PIXELS_PER_MINUTE,
                      bottom: 3,
                      transform: "translateX(-50%)",
                      color: "#94a3b8",
                      fontSize: 8,
                    }}
                  >
                    {min}
                  </div>
                ))}
              </div>

              {Array.from({ length: 60 }).map((_, min) => (
                <div
                  key={min}
                  style={{
                    position: "absolute",
                    left: min * PIXELS_PER_MINUTE,
                    top: 38,
                    width: min % 10 === 0 ? 1 : 0.5,
                    height: 100,
                    background: min % 10 === 0 ? "#e2e8f0" : "#f1f5f9",
                  }}
                />
              ))}
            </div>
          ))}

          {ads.map((ad, index) => {
            const startMinute = getMinutes(ad.startTime);
            const endMinute = getMinutes(ad.endTime);
            const duration = endMinute - startMinute;

            return (
              <Rnd
                key={ad.id}
                // enableResizing={false}
                enableResizing={{
                  left: true,
                  right: true,
                  top: false,
                  bottom: false,
                  topLeft: false,
                  topRight: false,
                  bottomLeft: false,
                  bottomRight: false,
                }}
                bounds="parent"
                resizeGrid={[PIXELS_PER_MINUTE * 5, 1]}
                dragGrid={[PIXELS_PER_MINUTE * 5, 1]}
                minWidth={20 * PIXELS_PER_MINUTE}
                size={{ width: duration * PIXELS_PER_MINUTE, height: 86 }}
                position={{ x: startMinute * PIXELS_PER_MINUTE, y: 48 }}
                onDragStop={(e, d) => updatePosition(index, d.x, duration * PIXELS_PER_MINUTE)}
                onResizeStop={(e, dir, ref, delta, pos) => {
                  updatePosition(index, pos.x, parseInt(ref.style.width));
                }}
                style={{
                  borderRadius: 18,
                  overflow: "hidden",
                  border: currentIndex === index ? "2px solid #3b82f6" : "1px solid #cbd5e1",
                  boxShadow: currentIndex === index
                    ? "0 0 25px rgba(59,130,246,0.3)"
                    : "0 6px 18px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                  background: "#ffffff",
                  cursor: "move",
                  zIndex: 20,
                }}
                onClick={() => onSelectVideo(index)}
              >
                <div style={{ width: "100%", height: "100%", position: "relative" }}>
                  {ad.type === "image" ? (
                    <img
                      src={ad.videoUrl}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <video
                      src={ad.videoUrl}
                      muted
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  )}

                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(to top, rgba(0,0,0,0.65), transparent)",
                    }}
                  />

                  <div
                    style={{
                      position: "absolute",
                      top: 8,
                      left: 10,
                      right: 10,
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {ad.title}
                  </div>

                  <div
                    style={{
                      position: "absolute",
                      bottom: 8,
                      left: 8,
                      background: "rgba(255,255,255,0.95)",
                      border: "1px solid #e2e8f0",
                      color: "#1e2937",
                      padding: "4px 10px",
                      borderRadius: 8,
                      fontSize: 9,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <FaClock size={8} />
                    {dayjs(ad.startTime).format("hh:mm A")}
                    {" → "}
                    {dayjs(ad.endTime).format("hh:mm A")}
                  </div>
                </div>
              </Rnd>
            );
          })}
        </div>
      </div>
    </div >
  );
};

export default TimelineEditor;

const iconBtn: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  background: "#ffffff",
  color: "#334155",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  transition: "all 0.2s ease",
};